import type { RuleId } from './finding';
import type { CaregiverEmployer, ContractorSector, IncomeSource, Sector, SpecialStatus, UserAnswers, VisaYear, WorkerType } from './answers';
import { incomeSourcesKey } from './answers';
import { form106Employers } from './employers';
import type { Form106Fields } from './form106';

/**
 * Which checks stay possible after each answer (Matt, 2026-10-22: "tell them on the step where we know"; design:
 * docs/specs/early-stop-table.md). The only place that says so: screens read it, none keeps an exception of its own.
 * Pure data and functions, no DOM.
 */

export type Goal = 'month' | 'several' | 'left' | 'form106';
export const GOALS: readonly Goal[] = ['month', 'several', 'left', 'form106'];
/** The cards of "Who are you?" (MATT GO 2026-10-23 13:40): `other` is "Another work arrangement" (the status `otherArrangement`). */
export type Who = 'israeli' | 'foreign' | 'other';
export const WHO: readonly Who[] = ['israeli', 'foreign', 'other'];
export type Doc = 'payslip' | 'form106';

/**
 * The engine's statusGuard answers `statusNotSupported` for every rule of a payslip at once (a stop), except that the
 * pension check alone stops for a youth or an apprentice (a partial case); a caregiver's answers take away pension and
 * severance (foreignGuard); a guarding or catering contractor's worker
 * loses the checks a sector rule may set differently (`sectorRules`: overtime), and keeps pension, severance and
 * recuperation as general floors (src/rules/status.ts contractorFloor; the youth and caregiver answers still take them);
 * one who also cleans loses the cleaning sector minimum (`cleaningMinimum`, its own row: ANSWER_EFFECTS.combination): so six names.
 */
export type PayslipCheck = 'pension' | 'severance' | 'recuperation' | 'sectorRules' | 'cleaningMinimum' | 'otherChecks';
/** Which payslip check name each rule belongs to (the engine-vs-table test reads it). */
export function payslipCheckOf(id: RuleId): PayslipCheck {
  if (id === 'pension' || id === 'severance' || id === 'recuperation') return id;
  if (id === 'cleaningMinimumWage') return 'cleaningMinimum';
  return SECTOR_RULES.includes(id) ? 'sectorRules' : 'otherChecks';
}
/**
 * Overtime: a sector rule may set it differently for every contractor's worker. (Vacation and pay on leaving run; only
 * their parts an agreement may set are left out, inside the check: contractorFloor. The cleaning sector minimum applies to
 * cleaning workers only: its own check name and row.)
 */
const SECTOR_RULES: readonly RuleId[] = ['overtime'];
/** The four cards of a Form 106 year (src/form106/signals.ts). */
export type Form106Check = 'partYear' | 'employers' | 'creditPoints' | 'estimate';
export type CheckId = PayslipCheck | Form106Check;
export const CHECKS: Record<Doc, readonly CheckId[]> = { payslip: ['pension', 'severance', 'recuperation', 'sectorRules', 'cleaningMinimum', 'otherChecks'], form106: ['partYear', 'employers', 'creditPoints', 'estimate'] };
export const docOf = (goal: Goal): Doc => (goal === 'form106' ? 'form106' : 'payslip');

/** The checks an answer takes away, per kind of document. `{}` = declared: this answer changes nothing. */
export type Effect = { payslip?: readonly PayslipCheck[]; form106?: readonly Form106Check[] };

const NO_PAYSLIP: Effect = { payslip: ['pension', 'severance', 'recuperation', 'sectorRules', 'cleaningMinimum', 'otherChecks'] };
/**
 * Guarding or institutional catering through a contractor (MATT GO 2026-10-22): the general legal checks as a floor; the
 * checks a sector rule may set differently are out (src/rules/status.ts contractorFloor; docs/review/contractor-floor-2026-10-22.md).
 */
const CONTRACTOR_FLOOR: Effect = { payslip: ['sectorRules'] };
/** Pension (and severance pay) is paid outside the payslip or its rules are an open question: caregivers employed by a company (foreignGuard). */
const NO_PENSION_SEVERANCE: Effect = { payslip: ['pension', 'severance'] };
/** Olim: what the month of aliyah means for a tax year's credit-point card (ANSWER_EFFECTS.aliyah). */
export type AliyahEffect = 'creditEnded' | 'afterYear';
export const ALIYAH_EFFECTS: readonly AliyahEffect[] = ['creditEnded', 'afterYear'];
/**
 * Credit points for a degree or a discharge (src/rules/creditExtra.ts): every situation ticked is a degree or a discharge whose points are over (or not
 * started) in a tax year, so that year's credit-point card has nothing to compare (ANSWER_EFFECTS.credit).
 */
export type CreditEffect = 'degree' | 'discharge' | 'both';
export const CREDIT_EFFECTS: readonly CreditEffect[] = ['degree', 'discharge', 'both'];
/** Answers that take a check away only together: the statuses each needs (ANSWER_EFFECTS.combination). */
export type Combination = 'contractorCleaning';
export const COMBINATIONS: Record<Combination, readonly SpecialStatus[]> = { contractorCleaning: ['contractor', 'cleaningContractor'] };
/** Mandatory pension starts at a minimum age (open item): a youth's and an apprentice's pension isn't checked (src/rules/status.ts). */
const NO_PENSION: Effect = { payslip: ['pension'] };
/** Foreign workers and asylum seekers: the Form 106 credit points and refund estimate are not modelled for them. */
const NO_CREDIT: Effect = { form106: ['creditPoints', 'estimate'] };
/**
 * "Another work arrangement" (MATT GO 2026-10-23 13:40): no check runs on any goal, a stop on "Who are you?" - except a
 * translation (TRANSLATION_ONLY, MATT OK 2026-10-23 18:52).
 */
const NOTHING: Effect = { payslip: ['pension', 'severance', 'recuperation', 'sectorRules', 'cleaningMinimum', 'otherChecks'], form106: ['partYear', 'employers', 'creditPoints', 'estimate'] };

/** Income a Form 106 cannot show and that may change the salary's tax: the whole year is out (params annualReport). */
const ANNUAL_REPORT: Effect = { form106: ['partYear', 'employers', 'creditPoints', 'estimate'] };

/** An unanswered question that blocks (a foreign worker who doesn't know their sector of work). */
const UNANSWERED: Effect = NO_PAYSLIP;

/**
 * One entry for EVERY value of every answer that could matter: a new status, sector of work or sector does not compile
 * until it has one (the types are Records), and the test checks the runtime lists. The group picked on "Who are you?"
 * (`Who`) adds no entry: `foreign` IS the status `foreignWorker` (the app keeps the two in step), `israeli` and `special`
 * change nothing by themselves (their statuses do).
 */
export const ANSWER_EFFECTS: {
  status: Record<SpecialStatus, Effect>;
  workerType: Record<WorkerType, Effect>;
  sector: Record<Sector, Effect>;
  /** A foreign caregiver's employer: an individual pays pension in lieu at the end of the job; for a company it is an open question (foreignGuard). */
  caregiverEmployer: Record<CaregiverEmployer, Effect>;
  /** Other income in a tax year (Form 106; params form106IncomeSources): what each kind takes away from that year's checks. */
  incomeSource: Record<IncomeSource, Effect>;
  /**
   * A contractor's worker: guarding or catering (read only while the `contractor` status is ticked; not given = a question,
   * no loss). Neither takes pension, severance or recuperation: the guarding order gives at least the general figures
   * (params guardingOrder), the catering order sets none of them (params cateringOrder; MATT GO 2026-10-23 02:08).
   */
  contractorSector: Record<ContractorSector, Effect>;
  /**
   * Two statuses together (`COMBINATIONS`): a guarding or catering contractor's worker who also cleans through a cleaning
   * company: which sector's minimum applies to the pay is not known, so the cleaning sector minimum isn't checked
   * (contractorFloor). Its own row and sentence, shown only when both are ticked (ux review 2026-10-23).
   */
  combination: Record<Combination, Effect>;
  /**
   * B2 (behind `__F106_GROUPS__`, on in every build since MATT GO 2026-10-26 04:58; docs/specs/b2-b3-form106-foreign-palestinian.md §2): a foreign worker's
   * visa in a tax year (asked on the questions step, per year): "not sure" takes that year's credit-point card (it asks
   * for the visa); the estimate still runs. Every other answer changes nothing. Absent only in a build with MAGIA_F106_GROUPS=0.
   */
  visaYear?: Record<VisaYear, Effect>;
  /**
   * Olim (behind `__OLIM__`, preview only; docs/specs/olim.md "Table row"): the month of aliyah (asked on the questions
   * step, under "new immigrant") puts the end of the new immigrant credit points before a tax year and no other
   * situation is ticked (`creditEnded`), or is after the tax year (`afterYear`, a month to fix): that year's credit-point
   * card is taken away (src/rules/olim.ts olimAliyahEffect), said on that step. Absent in a production build.
   */
  aliyah?: Record<AliyahEffect, Effect>;
  /**
   * Credit points for a degree (src/rules/creditExtra.ts; docs/specs/early-stop-table.md "Credit points: degrees"): the
   * year the studies ended (asked on the questions step, under "degree") puts every year of the degree's points before a
   * tax year, or after it, and nothing else is ticked: that year's credit-point card is taken away, said on that step.
   */
  credit: Record<CreditEffect, Effect>;
} = {
  status: {
    youth: NO_PENSION, apprentice: NO_PENSION, adjustedMinimum: {}, cleaningContractor: {}, oldAgePensioner: {},
    rehabilitation: NO_PAYSLIP, contractor: CONTRACTOR_FLOOR,
    foreignWorker: NO_CREDIT, otherArrangement: NOTHING,
  },
  workerType: {
    foreignCaregiver: {}, foreignAgriculture: {}, foreignConstruction: {}, foreignIndustry: {}, foreignCooking: {},
    // The employer's deposit is confirmed, but not whether it takes the place of pension and severance (foreignGuard): neither is checked.
    asylumSeeker: NO_PENSION_SEVERANCE,
    foreignOther: NO_PAYSLIP,
  },
  sector: { private: {}, public: NO_PAYSLIP },
  caregiverEmployer: { individual: { payslip: ['pension'] }, company: NO_PENSION_SEVERANCE },
  contractorSector: { guarding: {}, catering: {} },
  combination: { contractorCleaning: { payslip: ['cleaningMinimum'] } },
  ...((__F106_GROUPS__ || !import.meta.env.PROD) ? { visaYear: { b1Caregiving: {}, b1Other: {}, b1Both: {}, b1Part: {}, none: {}, notSure: { form106: ['creditPoints'] } } satisfies Record<VisaYear, Effect> } : {}),
  credit: { degree: { form106: ['creditPoints'] }, discharge: { form106: ['creditPoints'] }, both: { form106: ['creditPoints'] } },
  ...((__OLIM__ || !import.meta.env.PROD) ? { aliyah: { creditEnded: { form106: ['creditPoints'] }, afterYear: { form106: ['creditPoints'] } } satisfies Record<AliyahEffect, Effect> } : {}),
  incomeSource: {
    // Until that employer's Form 106 is added: a year with forms from 2 or more employers keeps the estimate (`employers`, ENOUGH_FORMS).
    otherEmployer: { form106: ['estimate'] }, niBenefits: {},
    pension: { form106: ['partYear', 'creditPoints', 'estimate'] },
    capitalAtSource: { form106: ['estimate'] }, rentSeparate: { form106: ['estimate'] },
    rentOther: ANNUAL_REPORT, selfEmployment: ANNUAL_REPORT, foreignIncome: ANNUAL_REPORT, otherIncome: ANNUAL_REPORT,
  },
};

/**
 * Answers that take away the goal itself, not single checks: on these goals every check is lost (a stop), and the goal
 * offered instead keeps the answers. `stillEmployed`: "No, I still work there" on the "I left my job" goal (MATT GO
 * 2026-10-22 19:40; the leaving checks say `notLeft` without a last day of work). Asked on the questions step.
 */
export const GOAL_ANSWERS: Record<'stillEmployed', { goals: readonly Goal[]; instead: Goal }> = {
  stillEmployed: { goals: ['left'], instead: 'month' },
};

/**
 * Whose documents (Matt, 2026-10-26 06:14; src/model/people.ts, src/upload/people.ts): documents of two or more people,
 * or ones whose ID number wasn't read, are told on the confirm step (src/ui/flowD/people.ts) as soon as both are read,
 * never on the results. Each takes no check away from the person checked (every check runs on their documents), so its
 * effect is `{}`; what it changes is which documents are checked: the others are set aside (`severalPeople`, except a form the user claims with `people.claim.action`, warned by `people.claim.warning`; an unread
 * one until it is answered "Yes"; with no ID read anywhere, all but the one picked until "all from one person"), so no
 * total ever adds them (reviewer 2026-10-26, money). The texts are
 * `<text>.<form106|payslip|documents>` (by what was loaded). Rows of docs/specs/early-stop-table.md "Whose documents".
 */
export const DOCUMENT_PEOPLE: Record<'severalPeople' | 'idUnread' | 'noIdRead', { effect: Effect; told: 'confirm'; text: string }> = {
  severalPeople: { effect: {}, told: 'confirm', text: 'people.many' },
  idUnread: { effect: {}, told: 'confirm', text: 'people.unknown' },
  noIdRead: { effect: {}, told: 'confirm', text: 'people.allOne' },
};

/**
 * The checks each question feeds (payslip goals). A question whose checks are all lost for this person is not asked
 * (persona walk 2026-10-22: never a question that feeds only what the table already took away). Questions not listed
 * feed checks no answer takes away by itself.
 */
export const QUESTION_FEEDS: Record<'hadPensionAtHire' | 'sex' | 'recuperationPayment', readonly PayslipCheck[]> = {
  // The pension waiting period (src/rules/pension.ts).
  hadPensionAtHire: ['pension'],
  // On the payslip questions, only the pension minimum age (the Form 106 asks its own, for the basic credit points).
  sex: ['pension'],
  // How recuperation pay is paid: only how the recuperation check compares (src/rules/recuperation.ts).
  recuperationPayment: ['recuperation'],
};

/** A question worth asking: some check it feeds is still possible for this goal and these answers. */
export function feedsSomething(question: keyof typeof QUESTION_FEEDS, goal: Goal, s: Situation, o: Options = {}): boolean {
  if (docOf(goal) !== 'payslip') return true;
  const possible = possibleChecks(goal, s, o).possible;
  return QUESTION_FEEDS[question].some((c) => possible.includes(c));
}

/**
 * The answers the table reads. The group picked on "Who are you?" is not one of them: `foreign` IS the status
 * `foreignWorker` (the app sets it when the group is picked and clears the group when the status is unticked), so there is
 * one source (Opus design review). `workerTypeNotSure`: a foreign worker said they don't know their sector of work, so
 * every payslip check asks for it (statusGuard) and none can run.
 */
export interface Situation {
  answers: Pick<UserAnswers, 'specialStatuses' | 'workerType' | 'sector' | 'caregiverEmployer' | 'contractorSector'>;
  workerTypeNotSure?: boolean;
  /** Form 106: the other-income answers, one list per tax year asked (a year voided by one of them is out; the goal stops only when every year is). */
  incomeSources?: readonly (readonly IncomeSource[])[];
  /**
   * Form 106: how many employers the loaded forms of each of those years come from (same order as `incomeSources`;
   * `form106Years` builds both). Not given → 1 per year.
   */
  employers?: readonly number[];
  /**
   * B2 (behind `__F106_GROUPS__`): a foreign worker's visa answer for each of those years (same order as `incomeSources`;
   * `form106Years` builds it). Not given → nothing taken away.
   */
  visaYears?: readonly (VisaYear | undefined)[];
  /**
   * Olim (behind `__OLIM__`): for each of those years (same order as `incomeSources`; one value for all the forms' years
   * while other income isn't answered), what the month of aliyah means for it (`form106Years` builds it from
   * src/rules/olim.ts olimAliyahEffect). Not given, or undefined for a year → nothing taken away.
   */
  olimAliyah?: readonly (AliyahEffect | undefined)[];
  /**
   * Credit points for a degree: for each of those years (same order as `incomeSources`; one value for all the forms' years
   * while other income isn't answered), whether the degree's points are over (src/rules/creditExtra.ts creditExtraEffect).
   */
  credit?: readonly (CreditEffect | undefined)[];
  /** "No, I still work there" (the questions step; GOAL_ANSWERS). */
  stillEmployed?: boolean;
  /** The payslip goal came from "Translate my payslip": a translation is asked for (TRANSLATION_ONLY). */
  translate?: boolean;
}

/**
 * An income-source answer whose loss ends once forms from this many employers are loaded in the year: another employer
 * takes the estimate away only until that employer's Form 106 is added (src/form106/estimate.ts, MATT GO 2026-10-22).
 */
const ENOUGH_FORMS: Partial<Record<IncomeSource, number>> = { otherEmployer: 2 };

/** The Form 106 part of a Situation: each tax year's other-income answers and how many employers its loaded forms come from. */
/** The one value all of `values` share, or undefined. */
const shared = <T>(values: readonly (T | undefined)[]): T | undefined => (values.length > 0 && values.every((v) => v === values[0]) ? values[0] : undefined);

export function form106Years(incomeSources: UserAnswers['incomeSources'], forms: readonly Pick<Form106Fields, 'taxYear' | 'deductionsFile' | 'employerName'>[], visaYears?: UserAnswers['visaYears'],
  /** Olim (behind `__OLIM__`): what the month of aliyah means for a tax year (src/rules/olim.ts olimAliyahEffect, bound to the answers and params). */
  olimEffect?: (year: number | undefined) => AliyahEffect | undefined,
  /** Credit points for a degree: what the answers mean for a tax year (src/rules/creditExtra.ts creditExtraEffect, bound to the answers and params). */
  creditEffect?: (year: number | undefined) => CreditEffect | undefined): Pick<Situation, 'incomeSources' | 'employers' | 'visaYears' | 'olimAliyah' | 'credit'> {
  const entries = Object.entries(incomeSources ?? {});
  const formYears = [...new Set(forms.map((f) => f.taxYear))];
  const yearOfKey = (key: string) => (key === incomeSourcesKey(undefined) ? undefined : Number(key));
  return {
    // Olim: per year asked; before other income is answered, one value: the effect every form's year shares (else none).
    ...(__OLIM__ && olimEffect ? { olimAliyah: entries.length ? entries.map(([key]) => olimEffect(yearOfKey(key))) : [shared(formYears.map(olimEffect))] } : {}),
    ...(creditEffect ? { credit: entries.length ? entries.map(([key]) => creditEffect(yearOfKey(key))) : [shared(formYears.map(creditEffect))] } : {}),
    incomeSources: entries.map(([, v]) => v),
    employers: entries.map(([key]) => form106Employers(forms.filter((f) => incomeSourcesKey(f.taxYear) === key))),
    // B2: each year's visa answer, in the same order.
    ...(__F106_GROUPS__ && visaYears ? { visaYears: entries.map(([key]) => visaYears[key]) } : {}),
  };
}
/** Options: `estimate`: the refund estimate exists in this build (__FORM106_ESTIMATE__); without it nothing is announced about it. */
export interface Options { estimate?: boolean }

/** An answer that took a check away: `kind` and `value` name the notice text (`flow.early.<kind>.<value>`). */
export interface Lost { check: CheckId; kind: 'status' | 'workerType' | 'sector' | 'unanswered' | 'caregiverEmployer' | 'incomeSource' | 'contractorSector' | 'combination' | 'visaYear' | 'aliyah' | 'credit' | 'goal'; value: string }

export interface Possible {
  doc: Doc;
  /** Tax years asked about (Form 106); 1 otherwise. */
  years: number;
  possible: CheckId[];
  /** In the order the engine names its reason: the sector first, then a status, then the sector of work. */
  lost: Lost[];
  /** Nothing the user came for stays possible. */
  stop: boolean;
  /** Something is lost, something stays. */
  partial: boolean;
}

/** The answers that are active, in the order the engine names its reason (statusGuard: public sector, unsupported status, then the sector of work). */
function activeAnswers(s: Situation): { kind: Exclude<Lost['kind'], 'goal'>; value: string }[] {
  const statuses = s.answers.specialStatuses ?? [];
  // "Another work arrangement" is the whole answer: an earlier sector or sector of work (hidden under that card) is not
  // read, so the notice names this card and a translation goes through (TRANSLATION_ONLY).
  if (statuses.includes('otherArrangement')) return [{ kind: 'status', value: 'otherArrangement' }];
  const foreign = statuses.includes('foreignWorker');
  return [
    ...(s.answers.sector ? [{ kind: 'sector' as const, value: s.answers.sector }] : []),
    ...statuses.map((value) => ({ kind: 'status' as const, value })),
    ...(foreign && s.answers.workerType ? [{ kind: 'workerType' as const, value: s.answers.workerType }] : []),
    ...(foreign && !s.answers.workerType && s.workerTypeNotSure ? [{ kind: 'unanswered' as const, value: 'workerType' }] : []),
    ...(foreign && s.answers.workerType === 'foreignCaregiver' && s.answers.caregiverEmployer ? [{ kind: 'caregiverEmployer' as const, value: s.answers.caregiverEmployer }] : []),
    // Not answered yet: a question (the checks ask for it), not a loss.
    ...(statuses.includes('contractor') && s.answers.contractorSector ? [{ kind: 'contractorSector' as const, value: s.answers.contractorSector }] : []),
    ...(Object.entries(COMBINATIONS) as [Combination, readonly SpecialStatus[]][]).filter(([, need]) => need.every((x) => statuses.includes(x))).map(([value]) => ({ kind: 'combination' as const, value })),
  ];
}

/**
 * Answers for which no check runs but a translation still does (MATT OK 2026-10-23 18:52: "Translate my payslip" goes
 * through for "Another work arrangement"; a translation checks nothing). On the translate goal they take nothing away
 * (no notice), the questions step is skipped (it only feeds checks: guardedStep) and the results show the translation only
 * (src/ui/app.ts renderPayslipResults).
 */
export const TRANSLATION_ONLY: readonly SpecialStatus[] = ['otherArrangement'];
/** A translation asked for by someone whose payslips we don't check. */
export const translationOnly = (s: Situation): boolean => {
  if (s.translate !== true) return false;
  if ((s.answers.specialStatuses ?? []).some((x) => TRANSLATION_ONLY.includes(x))) return true;
  // Audit 2026-10-24 (MATT GO 02:41, item 0): no "Who are you?" answer stops a translation (public sector, rehabilitation,
  // a foreign sector of work...): a translation checks nothing, so whatever would stop the checks leaves it alone.
  const rest: Situation = { ...s };
  delete rest.translate;
  delete rest.stillEmployed;
  return possibleChecks('month', rest).stop;
};

export function possibleChecks(goal: Goal, s: Situation, o: Options = {}): Possible {
  const doc = docOf(goal);
  // A translation only: the card is the whole answer (activeAnswers reads nothing else) and takes nothing from a
  // translation: nothing is checked, nothing announced.
  if (doc === 'payslip' && translationOnly(s)) return possibleChecks(goal, { answers: {} }, o);
  const effectOf = (kind: Exclude<Lost['kind'], 'goal'>, value: string): readonly CheckId[] =>
    // B2 (on unless MAGIA_F106_GROUPS=0): a foreign worker's credit points and refund are checked, so the status takes nothing away.
    kind === 'status' && value === 'foreignWorker' && __F106_GROUPS__ ? []
      : (kind === 'unanswered' ? UNANSWERED : (ANSWER_EFFECTS[kind] as Record<string, Effect> | undefined)?.[value])?.[doc] ?? [];
  // The goal doesn't fit the answer: every check lost, but only while the goal offered instead is possible; otherwise the
  // answers that block that one are the reason to name (and no goal is offered).
  const misfit = s.stillEmployed && GOAL_ANSWERS.stillEmployed.goals.includes(goal) && !possibleChecks(GOAL_ANSWERS.stillEmployed.instead, s, o).stop;
  const goalLost: Lost[] = misfit ? CHECKS[doc].map((check) => ({ check, kind: 'goal' as const, value: 'stillEmployed' })) : [];
  const base: Lost[] = [...goalLost, ...activeAnswers(s).flatMap(({ kind, value }) => effectOf(kind, value).map((check) => ({ check, kind, value })))];
  const all = CHECKS[doc].filter((c) => c !== 'estimate' || o.estimate !== false);
  // Form 106: each tax year has its own other-income answers; a check stays possible when it does in at least one year.
  const years: readonly (readonly IncomeSource[])[] = doc === 'form106' && s.incomeSources?.length ? s.incomeSources : [[]];
  const kept = (value: IncomeSource, year: number) => (s.employers?.[year] ?? 1) >= (ENOUGH_FORMS[value] ?? Infinity);
  // B2: a foreign worker (not an asylum seeker, who is not asked) not sure of a year's visa loses that year's credit-point card.
  const visaAsked = __F106_GROUPS__ && doc === 'form106' && (s.answers.specialStatuses ?? []).includes('foreignWorker') && s.answers.workerType !== 'asylumSeeker';
  const visaLost = (i: number): Lost[] => {
    const visa = __F106_GROUPS__ && visaAsked ? s.visaYears?.[i] : undefined;
    return visa ? effectOf('visaYear', visa).map((check) => ({ check, kind: 'visaYear' as const, value: visa })) : [];
  };
  // Olim: the month of aliyah takes that year's credit-point card (the credit ended, or the month is after the year).
  const aliyahLost = (i: number): Lost[] => {
    const value = __OLIM__ && doc === 'form106' ? s.olimAliyah?.[i] : undefined;
    return value ? effectOf('aliyah', value).map((check) => ({ check, kind: 'aliyah' as const, value })) : [];
  };
  // Credit points for a degree: the degree's points are over (or not started) in that year.
  const creditLost = (i: number): Lost[] => {
    const value = doc === 'form106' ? s.credit?.[i] : undefined;
    return value ? effectOf('credit', value).map((check) => ({ check, kind: 'credit' as const, value })) : [];
  };
  const perYear: Lost[][] = years.map((sources, i) => [...base, ...sources.filter((value) => !kept(value, i)).flatMap((value) => effectOf('incomeSource', value).map((check) => ({ check, kind: 'incomeSource' as const, value }))), ...visaLost(i), ...aliyahLost(i), ...creditLost(i)]);
  const possible = all.filter((c) => perYear.some((lost) => !lost.some((l) => l.check === c)));
  const lost = [...new Map(perYear.flat().filter((l) => all.includes(l.check)).map((l) => [`${l.check}.${l.kind}.${l.value}`, l])).values()];
  return { doc, years: years.length, possible, lost, stop: possible.length === 0, partial: lost.length > 0 && possible.length > 0 };
}

/** The goal to offer instead of a stopped one, or undefined. `form106Open`: the Form 106 goal is open in this build. */
export function alternative(goal: Goal, s: Situation, form106Open: boolean, o: Options = {}): Goal | undefined {
  // The goal itself doesn't fit the answer: the goal it names instead (with the same answers), when that one is possible.
  const instead = s.stillEmployed && GOAL_ANSWERS.stillEmployed.goals.includes(goal) ? GOAL_ANSWERS.stillEmployed.instead : undefined;
  if (instead) return possibleChecks(instead, s, o).stop ? undefined : instead;
  const other: Goal = goal === 'form106' ? 'month' : 'form106';
  if (other === 'form106' && !form106Open) return undefined;
  return possibleChecks(other, s, o).stop ? undefined : other;
}

/**
 * What is known on a step: "Who are you?" asks the groups, statuses, sector of work, caregiver's employer and (payslip
 * goals) the sector; the Form 106's other income is first asked on the questions step (the tax years come from the
 * forms), so "Who are you?" does not read it - an answer given later and left behind by Back must not trap the person on
 * a step that cannot change it.
 */
export function atStep(step: 'who' | 'questions', s: Situation): Situation {
  if (step === 'questions') return s;
  const rest = { ...s };
  delete rest.incomeSources;
  delete rest.stillEmployed;
  // B2: the visa is asked per tax year, after the upload.
  if (__F106_GROUPS__) delete rest.visaYears;
  // Olim: the month of aliyah is asked on the questions step.
  if (__OLIM__) delete rest.olimAliyah;
  // Credit points for a degree: the year the studies ended is asked on the questions step too.
  delete rest.credit;
  return rest;
}

/** The i18n key of the sentence for an answer that took a check away; without the refund estimate in the build, the credit-points-only sentence. */
export function noticeKey(l: Lost, o: Options = {}): string {
  const base = `flow.early.${l.kind}.${l.value}`;
  if (l.kind === 'incomeSource' && l.value === 'pension' && o.estimate === false) return `${base}NoEstimate`;
  return l.kind === 'status' && l.value === 'foreignWorker' && o.estimate === false ? `${base}CreditOnly` : base;
}

/**
 * Answers we deliberately don't check, with no "We plan to add it" (Matt, 2026-10-22 19:54, item 7;
 * docs/research/plan-notices-and-guards-2026-10-22.md): their stop sentence says so plainly, and the sentence
 * "You can still check your Form 106." follows it only when that button is offered (`alternative`).
 */
const NO_PLAN: ReadonlySet<string> = new Set(['status.rehabilitation', 'sector.public', 'status.otherArrangement']);

const noPlan = (p: Possible) => p.stop && !!p.lost[0] && NO_PLAN.has(`${p.lost[0].kind}.${p.lost[0].value}`);

/**
 * The stop notice's heading: the goal that doesn't fit the answer says so; a deliberate exclusion has no "yet" (the
 * reason sentence has none either); otherwise "We can't check your ... yet".
 */
export function stopHeadingKey(p: Possible): string {
  if (p.lost[0]?.kind === 'goal') return `flow.early.stop.heading.${p.lost[0].value}`;
  return `flow.early.stop.heading.${p.doc}${noPlan(p) ? '.noPlan' : ''}`;
}

/** The sentence after a stop's reason that points to the alternative goal, or undefined (shown only with its button). */
export function altKey(p: Possible, alt: Goal | undefined): string | undefined {
  return noPlan(p) && alt === 'form106' ? 'flow.early.alt.form106' : undefined;
}

/** The "what we still check" sentence for a partial case. */
export function stillKey(p: Possible): string {
  if (p.doc === 'payslip') return 'flow.early.still.otherChecks';
  // Several years, one of them losing something: the line must not read as covering that year.
  if (p.years > 1 && p.lost.some((l) => l.kind === 'incomeSource' || l.kind === 'visaYear' || l.kind === 'aliyah' || l.kind === 'credit')) return 'flow.early.still.otherYears';
  return `flow.early.still.${p.possible.join('.')}`;
}

export type FlowStepId = 'goal' | 'who' | 'upload' | 'questions' | 'confirm' | 'results';
export const FLOW_STEP_IDS: readonly FlowStepId[] = ['goal', 'who', 'upload', 'questions', 'confirm', 'results'];

/**
 * The step that is really shown when `step` is asked for - by Continue, Back, Forward, an Edit link, the start screen
 * or a restored page (the app's stage() calls this, nothing else decides). A goal that is already impossible never
 * shows the upload, the confirm step or the results: it shows the step with the stop notice ("Who are you?" when its
 * answers alone make it a stop, else the questions step, which shows only the stop and the answers that caused it).
 */
export function guardedStep(step: FlowStepId, goal: Goal | undefined, s: Situation, o: Options = {}): FlowStepId {
  if (!goal || step === 'goal' || step === 'who') return step;
  // A translation only: the questions feed checks that don't run, so "Check your answers" comes next.
  if (docOf(goal) === 'payslip' && translationOnly(s) && step === 'questions') return 'confirm';
  if (!possibleChecks(goal, s, o).stop) return step;
  return step === 'upload' && possibleChecks(goal, atStep('who', s), o).stop ? 'who' : 'questions';
}
/**
 * The step after Continue; `stop` when nothing the user came for stays possible. Every Continue goes through here.
 * `payslipsRead`: payslips are already loaded (the upload is skipped).
 */
export function stepAfter(step: 'who' | 'questions', goal: Goal, s: Situation, payslipsRead = false, o: Options = {}): FlowStepId | 'stop' {
  if (possibleChecks(goal, atStep(step, s), o).stop) return 'stop';
  return step === 'who' ? (payslipsRead ? 'questions' : 'upload') : 'confirm';
}

/**
 * Every key of the user's answers, classed: `table` = read by this table; `none` = changes what is possible in no way
 * (the reason is the comment); `open` = does change something, not modelled yet (listed in docs/specs/early-stop-table.md
 * "Other places"). A new answer key does not compile until it is classed here (and the test checks the open items are
 * in the doc).
 */
export const ANSWER_KEYS: Record<keyof UserAnswers, 'table' | { none: string } | { open: string }> = {
  specialStatuses: 'table', workerType: 'table', sector: 'table', caregiverEmployer: 'table', incomeSources: 'table', contractorSector: 'table',
  hadPensionAtHire: { none: 'asked as a question; it only picks the pension waiting period' },
  payBasis: { none: 'asked as a question when the payslip does not show it' },
  jobPercent: { none: 'asked as a question when the payslip does not print it' },
  recuperationPayment: { none: 'asked as a question; it only picks how recuperation is compared' },
  birthDate: { none: 'asked as a question; a youth is a status (table), the date only sets the rate' },
  sex: { none: 'asked as a question; only the pension age and the basic credit points' },
  sexDeclined: { none: 'only remembers that "Prefer not to say" was chosen (the sex question)' },
  adjustedMinimumPercent: { none: 'asked as a question; only the minimum wage percentage' },
  unpaidLeave: { none: 'asked as a question; only the minimum wage comparison' },
  unpaidLeaveMonths: { none: 'follow-up of unpaidLeave' },
  taxStatuses: { none: 'asked as a question; only the credit-points comparison (which the table already removes for some groups)' },
  childrenBirthYears: { none: 'follow-up of taxStatuses' },
  // Olim (behind __OLIM__): the credit ended before the tax year, or the month is after it (ANSWER_EFFECTS.aliyah); without the flag, only the estimate's points.
  aliyahMonth: 'table',
  // Credit points for a degree: its points over (or not started) before the tax year take that year's card away (ANSWER_EFFECTS.credit).
  degreeKind: 'table', degreeYear: 'table', degreeStudyYears: 'table', dischargeMonth: 'table',
  serviceLong: { none: 'asked as a question; only the rate of a discharged soldier\'s points (the lower rate when not answered)' },
  disabledChildren: { none: 'asked as a question; only the estimate\'s credit points (high end only; not counted when not answered)' },
  singleParentKind: { none: 'asked as a question; only the estimate\'s credit points (not counted when not answered)' },
  olimFromRelative: { none: 'olim (behind __OLIM__): only the exemption pointer\'s notes' },
  niCertificates: { none: 'follow-up of incomeSources' },
  workWeekDays: { none: 'asked as a question; only vacation and sick days' },
  employmentEndDate: { none: 'asked as a question; the leaving checks say "not applicable" without it' },
  leavingReason: { none: 'follow-up of employmentEndDate' },
  noticeDate: { none: 'follow-up of employmentEndDate' },
  housingRegion: { none: 'asked as a question; only the housing deduction cap' },
  visaYears: 'table',
  monthsInIsrael: { none: 'only the proration of the estimate; not sure has a stated safer assumption' },
};
